<template>
    <div class="message-detail">
        <confirm
            id="confirmDeleteNote"
            ref="confirmDeleteNote"
            @confirm="deleteNote"
        >
            <template slot="title">
                <span>
                    {{ t("inbox-message-confirm_delete_title") }}
                </span>
            </template>
            <template slot="body">
                <div>
                    {{ t("inbox-message-confirm_delete_body") }}
                </div>
            </template>
        </confirm>

        <confirm
            id="confirmDeleteAnswer"
            ref="confirmDeleteAnswer"
            @confirm="softDeleteAnswer"
        >
            <template slot="title">
                <span>{{ t("inbox-message-confirm_delete_answer_title") }}</span>
            </template>
            <template slot="body">
                <div>{{ t("inbox-message-confirm_delete_answer_body") }}</div>
            </template>
        </confirm>

        <kv-modal ref="editAnswersModal" size="large">
            <template #title>
                {{
                    editMode === "duplicate"
                        ? t("inbox-message-duplicate_modal_title")
                        : t("inbox-message-edit_modal_title")
                }}
            </template>
            <template #body>
                <div class="edit-answers-form">
                    <div
                        class="edit-answer-row"
                        v-for="answer in editableAnswers"
                        :key="answer.forms_steps_questions_id"
                    >
                        <kv-typography
                            weight="medium"
                            size="small"
                            color="muted"
                            class="edit-question"
                        >
                            {{ answer.question }}
                        </kv-typography>

                        <select
                            v-if="answer.type === 'boolean'"
                            class="form-control"
                            v-model="editDraft[answer.forms_steps_questions_id]"
                        >
                            <option v-if="!answer.id" value="">
                                {{ t("inbox-message-unanswered") }}
                            </option>
                            <option :value="1">
                                {{ t("inbox-message-bool_yes") }}
                            </option>
                            <option :value="0">
                                {{ t("inbox-message-bool_no") }}
                            </option>
                        </select>

                        <select
                            v-else-if="answer.type === 'emotion'"
                            class="form-control"
                            v-model="editDraft[answer.forms_steps_questions_id]"
                        >
                            <option v-if="!answer.id" value="">
                                {{ t("inbox-message-unanswered") }}
                            </option>
                            <option
                                v-for="n in 5"
                                :key="n"
                                :value="n"
                            >
                                {{ n }}
                            </option>
                        </select>

                        <select
                            v-else-if="answer.type === 'nps'"
                            class="form-control"
                            v-model="editDraft[answer.forms_steps_questions_id]"
                        >
                            <option v-if="!answer.id" value="">
                                {{ t("inbox-message-unanswered") }}
                            </option>
                            <option
                                v-for="n in npsOptions"
                                :key="n"
                                :value="n"
                            >
                                {{ n }}
                            </option>
                        </select>

                        <select
                            v-else-if="answer.type === 'ces'"
                            class="form-control"
                            v-model="editDraft[answer.forms_steps_questions_id]"
                        >
                            <option v-if="!answer.id" value="">
                                {{ t("inbox-message-unanswered") }}
                            </option>
                            <option
                                v-for="opt in cesOptions"
                                :key="opt.value"
                                :value="opt.value"
                            >
                                {{ opt.value }} — {{ opt.label }}
                            </option>
                        </select>

                        <kv-textarea
                            v-else-if="
                                ['comment', 'input', 'selectbox'].includes(
                                    answer.type,
                                )
                            "
                            v-model="editDraft[answer.forms_steps_questions_id]"
                            :rows="3"
                            resize
                        />

                        <kv-input
                            v-else-if="answer.type === 'date'"
                            v-model="editDraft[answer.forms_steps_questions_id]"
                        />

                        <div v-else-if="answer.type === 'table'" class="edit-table">
                            <table-answer-input
                                v-if="editTableSettings(answer) && editDraft[answer.forms_steps_questions_id]"
                                :settings="editTableSettings(answer)"
                                v-model="editDraft[answer.forms_steps_questions_id].values"
                            />
                            <kv-typography v-else color="muted" size="small">
                                {{ t("inbox-message-table_edit_unavailable") }}
                            </kv-typography>
                        </div>

                        <div v-else-if="answer.type === 'file'" class="edit-files">
                            <div
                                v-for="(file, i) in draftFiles(answer)"
                                :key="i"
                                class="edit-file-row"
                            >
                                <a
                                    v-if="file.url && showsEditPreview(file)"
                                    class="edit-file-thumb"
                                    :href="file.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        :src="file.url"
                                        :alt="file.name"
                                        loading="lazy"
                                        @error="editPreviewFailed(file)"
                                    />
                                </a>
                                <a
                                    v-if="file.url"
                                    class="edit-file-name"
                                    :href="file.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {{ file.name || file.url }}
                                </a>
                                <span v-else>{{ file.name || t("inbox-message-no_files") }}</span>
                                <button
                                    type="button"
                                    class="remove-file"
                                    :disabled="!!uploadingEditFiles[answer.forms_steps_questions_id]"
                                    @click.prevent="removeEditFile(answer, i)"
                                >
                                    {{ t("inbox-message-remove_file") }}
                                </button>
                            </div>
                            <kv-typography
                                v-if="!draftFiles(answer).length"
                                color="muted"
                                size="small"
                            >
                                {{ t("inbox-message-no_files") }}
                            </kv-typography>
                            <label
                                class="edit-file-upload"
                                :class="{
                                    disabled:
                                        !!uploadingEditFiles[
                                            answer.forms_steps_questions_id
                                        ] || !canAddEditFiles(answer),
                                }"
                            >
                                <input
                                    type="file"
                                    :multiple="editFileMaxCount(answer) > 1"
                                    :accept="editFileAccept(answer)"
                                    :disabled="
                                        !!uploadingEditFiles[
                                            answer.forms_steps_questions_id
                                        ] || !canAddEditFiles(answer)
                                    "
                                    @change="onEditFilesSelected($event, answer)"
                                />
                                <span v-if="uploadingEditFiles[answer.forms_steps_questions_id]">
                                    {{ t("inbox-message-uploading_files") }}
                                </span>
                                <span v-else>{{ t("inbox-message-upload_files") }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer="{ toggle }">
                <kv-button variant="outlined" @click="closeEditModal(toggle)">
                    {{ t("inbox-message-cancel") }}
                </kv-button>
                <kv-button
                    :loading="savingEdit"
                    :disabled="savingEdit || isUploadingEditFiles"
                    @click="saveEditedAnswers(toggle)"
                >
                    {{
                        editMode === "duplicate"
                            ? t("inbox-message-duplicate_save")
                            : t("inbox-message-edit_save")
                    }}
                </kv-button>
            </template>
        </kv-modal>

        <create-task-modal
            ref="createTaskModal"
            @saved="onCreateTaskSaved"
        />

        <div class="detail-toolbar">
            <div class="detail-toolbar-top">
                <div
                    class="detail-title"
                    v-if="status !== 'loading' && message.id"
                >
                    <div class="detail-title-text">
                        <component
                            :is="
                                $store.dispatch('hasPermission', 'reports')
                                    ? 'router-link'
                                    : 'span'
                            "
                            class="detail-title-link"
                            :to="{
                                path: '/report',
                                query: {
                                    filter: JSON.stringify({
                                        forms: message.forms_id,
                                    }),
                                },
                            }"
                            :title="t('inbox-message-answers_report_title')"
                        >
                            <kv-typography title size="medium" weight="medium">
                                {{
                                    t("inbox-message-form_title", [
                                        message.form_name,
                                    ])
                                }}
                            </kv-typography>
                        </component>
                        <kv-typography
                            class="detail-filled"
                            color="muted"
                            size="extra-small"
                        >
                            {{ t("inbox-message-filled_on") }}
                            <human-date
                                :date="message.created"
                                :format="t('dates-format_date_time')"
                            />
                        </kv-typography>
                    </div>
                </div>
                <kv-skeleton
                    v-else
                    height="22px"
                    style="width: 55%; flex: 1"
                />
                <div class="detail-toolbar-actions">
                    <kv-tooltip
                        v-if="
                            status !== 'loading' &&
                            message.id &&
                            canCreateTask
                        "
                        :text="t('inbox-message-create_task')"
                    >
                        <kv-button
                            variant="tonal"
                            size="small"
                            prepend-icon="ui-check-square-broken"
                            icon-size="2"
                            :disabled="mutating"
                            @click="openCreateTaskModal"
                        />
                    </kv-tooltip>
                    <kv-tooltip
                        v-if="status !== 'loading' && message.id"
                        :text="t('inbox-message-mark_unread')"
                    >
                        <kv-button
                            variant="tonal"
                            size="small"
                            prepend-icon="ui-mail-02"
                            icon-size="2"
                            :disabled="readField.disabled || mutating"
                            @click="markAsUnread"
                        />
                    </kv-tooltip>
                    <div
                        v-if="status !== 'loading' && message.id"
                        class="detail-more-menu"
                        v-click-outside="closeActionsMenu"
                    >
                        <kv-tooltip :text="t('inbox-message-more_actions')">
                            <kv-button
                                variant="tonal"
                                size="small"
                                :disabled="mutating"
                                :aria-label="t('inbox-message-more_actions')"
                                :aria-expanded="actionsMenuOpen ? 'true' : 'false'"
                                @click="toggleActionsMenu"
                            >
                                <span class="far fa-ellipsis-v"></span>
                            </kv-button>
                        </kv-tooltip>
                        <div
                            v-if="actionsMenuOpen"
                            class="detail-more-dropdown"
                            role="menu"
                        >
                            <button
                                type="button"
                                class="detail-more-item"
                                role="menuitem"
                                :disabled="mutating"
                                @click="runMenuAction(openEditAnswers)"
                            >
                                <Icon icon-class="ui-edit-05" size="3" />
                                <span>{{ t("inbox-message-edit_answers") }}</span>
                            </button>
                            <button
                                type="button"
                                class="detail-more-item"
                                role="menuitem"
                                @click="runMenuAction(printAnswerSheet)"
                            >
                                <Icon icon-class="ui-download-01" size="3" />
                                <span>{{ t("inbox-message-print_answer") }}</span>
                            </button>
                            <button
                                type="button"
                                class="detail-more-item"
                                role="menuitem"
                                :disabled="mutating"
                                @click="runMenuAction(openDuplicateAnswers)"
                            >
                                <Icon icon-class="ui-copy-03" size="3" />
                                <span>{{ t("inbox-message-duplicate_answer") }}</span>
                            </button>
                            <button
                                type="button"
                                class="detail-more-item is-danger"
                                role="menuitem"
                                :disabled="mutating"
                                @click="
                                    runMenuAction(() =>
                                        $refs.confirmDeleteAnswer.open(),
                                    )
                                "
                            >
                                <Icon icon-class="ui-trash-01" size="3" />
                                <span>{{ t("inbox-message-delete_answer") }}</span>
                            </button>
                        </div>
                    </div>
                    <button
                        type="button"
                        class="detail-close"
                        :aria-label="t('inbox-message-back')"
                        @click="closeMessage"
                    >
                        <Icon icon-class="ui-x-close" size="4" />
                    </button>
                </div>
            </div>
            <div class="detail-tags">
                <answer-tags
                    v-if="status !== 'loading' && message.id"
                    :tags="message.tags || []"
                    :suggestions="mergedSuggestions"
                    :busy="tagField.disabled"
                    @apply="applyTag"
                    @remove="removeTag"
                    @update="updateTagDefinition"
                    @delete="deleteTagDefinition"
                />
                <kv-skeleton v-else height="28px" style="width: 96px" />
            </div>
        </div>

        <div v-if="status === 'loading' || !message.id" class="detail-stack">
            <kv-card v-if="!hideClientSection">
                <template #title>
                    <kv-skeleton height="28px" style="width: 55%" />
                </template>
                <template #subtitle>
                    <kv-skeleton height="12px" style="width: 35%" />
                </template>
                <template #text>
                    <div class="d-flex flex-column gap-md">
                        <div
                            class="d-flex justify-content-between"
                            v-for="i in 4"
                            :key="i"
                        >
                            <kv-skeleton style="width: 30%" height="16px" />
                            <kv-skeleton style="width: 45%" height="16px" />
                        </div>
                    </div>
                </template>
            </kv-card>
            <kv-card>
                <template #title>
                    <kv-skeleton height="18px" style="width: 110px" />
                </template>
                <template #text>
                    <kv-skeleton :rows="3" />
                </template>
            </kv-card>
        </div>

        <div v-else class="detail-stack">
            <kv-card v-if="!hideClientSection" class="identity-card">
                <template #title>
                    <div class="identity-title">
                        <router-link
                            class="detail-name-link"
                            :to="'/client/' + message.clients_id"
                        >
                            <kv-typography
                                title
                                size="extra-large"
                                weight="medium"
                                hover
                                truncate
                            >
                                {{ message.first_name }} {{ message.last_name }}
                            </kv-typography>
                        </router-link>
                        <div class="identity-title-actions">
                            <kv-chip
                                v-if="rfmInfo && rfmInfo.name"
                                class="text-nowrap"
                                size="small"
                                density="extra-compact"
                                :colors="{
                                    text: rfmInfo.textColor,
                                    background: rfmInfo.color,
                                }"
                                :prepend-icon="rfmInfo.icon"
                            >
                                {{ rfmInfo.name }}
                            </kv-chip>
                            <kv-button
                                variant="outlined"
                                size="small"
                                prepend-icon="ui-user-01"
                                icon-size="2"
                                :to="'/client/' + message.clients_id"
                            >
                                {{ t("inbox-message-view_client") }}
                            </kv-button>
                        </div>
                    </div>
                </template>

                <template #text>
                    <div class="detail-facts">
                        <div class="fact-row">
                            <kv-typography color="muted" size="small">
                                {{ t("inbox-message-phone_label") }}
                            </kv-typography>
                            <div class="fact-value">
                                <btn-whatsapp
                                    v-if="message.whatsapp"
                                    :number="message.whatsapp"
                                ></btn-whatsapp>
                                <kv-typography weight="medium" size="small">
                                    {{
                                        message.whatsapp
                                            ? message.whatsapp
                                            : t("inbox-message-not_informed")
                                    }}
                                </kv-typography>
                            </div>
                        </div>
                        <div class="fact-row">
                            <kv-typography color="muted" size="small">
                                {{ t("inbox-message-email_label") }}
                            </kv-typography>
                            <kv-typography weight="medium" size="small" truncate>
                                {{
                                    message.email
                                        ? message.email
                                        : t("inbox-message-not_informed")
                                }}
                            </kv-typography>
                        </div>
                        <div class="fact-row" v-if="message.external_id">
                            <kv-typography color="muted" size="small">
                                {{ t("inbox-message-external_id_label") }}
                            </kv-typography>
                            <kv-typography weight="medium" size="small" truncate>
                                {{ message.external_id
                                }}<template v-if="message.external_source">
                                    ({{ message.external_source }})</template
                                >
                            </kv-typography>
                        </div>
                        <div class="fact-row" v-if="message.branch_name">
                            <kv-typography color="muted" size="small">
                                {{ t("filter_bar-branches") }}
                            </kv-typography>
                            <kv-typography weight="medium" size="small">
                                {{ message.branch_name }}
                            </kv-typography>
                        </div>
                        <div class="fact-row">
                            <kv-typography color="muted" size="small">
                                {{ t("inbox-message-last_purchase_label") }}
                            </kv-typography>
                            <kv-typography weight="medium" size="small">
                                <human-date
                                    v-if="message.date_last_order"
                                    :date="message.date_last_order"
                                    :format="t('dates-format_date')"
                                />
                                <template v-else>
                                    {{ t("inbox-message-not_informed") }}
                                </template>
                            </kv-typography>
                        </div>
                        <div class="fact-row">
                            <kv-typography color="muted" size="small">
                                {{ t("inbox-message-purchases_label") }}
                            </kv-typography>
                            <kv-typography weight="medium" size="small">
                                <template
                                    v-if="
                                        message.total_spend &&
                                        message.number_orders
                                    "
                                >
                                    {{
                                        t("inbox-message-spend_summary", [
                                            parseFloat(
                                                message.total_spend,
                                            ).toLocaleString() || 0,
                                            message.number_orders || 0,
                                        ])
                                    }}
                                </template>
                                <template v-else>
                                    {{ t("inbox-message-no_purchase_data") }}
                                </template>
                            </kv-typography>
                        </div>
                    </div>
                </template>

            </kv-card>

            <linked-answer-panel
                :answer="linkedAnswerPanelData"
                :title="t('inbox-message-answers_title')"
                :default-open="true"
            />

            <div
                class="linked-tasks-section"
                v-if="message.tasks && message.tasks.length"
            >
                <kv-typography
                    weight="medium"
                    color="secondary"
                    size="medium"
                    class="notes-title"
                >
                    {{ t("inbox-message-linked_tasks_title") }}
                </kv-typography>
                <div class="linked-tasks-list">
                    <router-link
                        v-for="task in message.tasks"
                        :key="task.id"
                        class="linked-task-card"
                        :class="linkedTaskCardClass(task)"
                        :to="linkedTaskRoute(task)"
                    >
                        <div class="linked-task-top">
                            <div class="linked-task-badges">
                                <kv-chip
                                    v-if="task.completed_at"
                                    size="small"
                                    density="extra-compact"
                                    color="success"
                                    prepend-icon="ui-check-circle"
                                >
                                    {{ t("tasks-completed_badge") }}
                                </kv-chip>
                                <kv-chip
                                    v-else-if="Number(task.archived) === 1"
                                    size="small"
                                    density="extra-compact"
                                    color="muted"
                                >
                                    {{ t("tasks-archived_badge") }}
                                </kv-chip>
                                <kv-chip
                                    v-else
                                    size="small"
                                    density="extra-compact"
                                    color="primary"
                                >
                                    {{ t("inbox-message-linked_task_open") }}
                                </kv-chip>
                                <kv-chip
                                    v-if="Number(task.priority) >= 2"
                                    size="small"
                                    density="extra-compact"
                                    :color="
                                        Number(task.priority) >= 3
                                            ? 'error'
                                            : 'warning'
                                    "
                                    prepend-icon="ui-flag-01"
                                >
                                    {{ linkedTaskPriorityLabel(task) }}
                                </kv-chip>
                                <div
                                    v-if="task.board_name || task.column_name"
                                    class="linked-task-meta-item"
                                >
                                    <Icon icon-class="ui-list" size="1.5" />
                                    <span>
                                        <template v-if="task.board_name">
                                            {{ task.board_name }}
                                        </template>
                                        <template
                                            v-if="
                                                task.board_name &&
                                                task.column_name
                                            "
                                        >
                                            ·
                                        </template>
                                        <template v-if="task.column_name">
                                            {{ task.column_name }}
                                        </template>
                                    </span>
                                </div>
                                <div class="linked-task-meta-item">
                                    <Icon icon-class="ui-user-01" size="1.5" />
                                    <span>
                                        {{
                                            task.assignee_name ||
                                            t(
                                                "inbox-message-linked_task_unassigned",
                                            )
                                        }}
                                    </span>
                                </div>
                                <div
                                    v-if="task.due_date"
                                    class="linked-task-meta-item"
                                    :class="linkedTaskDueClass(task)"
                                >
                                    <Icon
                                        icon-class="ui-calendar"
                                        size="1.5"
                                    />
                                    <span class="linked-task-due">
                                        <human-date
                                            :date="task.due_date"
                                            :format="t('dates-format_date')"
                                        />
                                    </span>
                                </div>
                            </div>
                            <Icon
                                icon-class="ui-chevron-right"
                                size="2"
                                class="linked-task-open-icon"
                            />
                        </div>

                        <kv-typography
                            size="small"
                            weight="medium"
                            class="linked-task-title"
                        >
                            {{ task.title || "#" + task.id }}
                        </kv-typography>
                        <div
                            v-if="task.files && task.files.length"
                            class="linked-task-files"
                            @click.prevent.stop
                        >
                            <span
                                v-for="file in task.files"
                                :key="file.id"
                                class="linked-task-file"
                                role="link"
                                tabindex="0"
                                @click.prevent.stop="openTaskFile(file)"
                                @keydown.enter.prevent.stop="openTaskFile(file)"
                            >
                                <Icon icon-class="ui-download-01" size="1.5" />
                                {{ file.name }}
                            </span>
                        </div>
                    </router-link>
                </div>
            </div>

            <div class="notes-section">
                <kv-typography
                    weight="medium"
                    color="secondary"
                    size="medium"
                    class="notes-title"
                >
                    {{ t("inbox-message-notes_title") }}
                </kv-typography>

                <kv-card
                    v-for="noteItem in message.notes"
                    :key="noteItem.id"
                    variant="tonal"
                    density="compact"
                    class="note-card"
                >
                    <template #title>
                        <div class="note-header">
                            <div class="note-meta">
                                <kv-chip size="small" density="extra-compact">
                                    {{ noteItem.user_name }}
                                </kv-chip>
                                <kv-typography
                                    color="muted"
                                    size="extra-small"
                                >
                                    <human-date
                                        :date="noteItem.created_at"
                                        :format="t('dates-format_date_time')"
                                    />
                                </kv-typography>
                            </div>
                            <div
                                class="note-actions"
                                v-if="
                                    editingNoteId !== noteItem.id &&
                                    noteItem.users_id === user.id
                                "
                            >
                                <kv-tooltip :text="t('inbox-message-edit_note')">
                                    <kv-button
                                        variant="text"
                                        size="small"
                                        prepend-icon="ui-edit-05"
                                        icon-size="2"
                                        :disabled="savingNote"
                                        @click="editNote(noteItem)"
                                    />
                                </kv-tooltip>
                                <kv-tooltip
                                    :text="t('inbox-message-delete_note')"
                                >
                                    <kv-button
                                        variant="text"
                                        size="small"
                                        prepend-icon="ui-trash-01"
                                        icon-size="2"
                                        :disabled="savingNote"
                                        @click="confirmDeleteNote(noteItem)"
                                    />
                                </kv-tooltip>
                            </div>
                        </div>
                    </template>
                    <template #text>
                        <kv-typography
                            v-if="editingNoteId !== noteItem.id"
                            size="small"
                            line-break
                        >
                            <span v-html="nl2br(noteItem.note)"></span>
                        </kv-typography>

                        <div v-else class="note-edit">
                            <kv-textarea
                                v-model="editingNoteText"
                                :disabled="savingNote"
                                :rows="3"
                                resize
                            />
                            <div class="edit-actions">
                                <kv-button
                                    size="small"
                                    :loading="savingNote"
                                    :disabled="
                                        savingNote || !editingNoteText.trim()
                                    "
                                    @click="saveEditedNote"
                                >
                                    {{ t("inbox-message-save") }}
                                </kv-button>
                                <kv-button
                                    size="small"
                                    variant="outlined"
                                    :disabled="savingNote"
                                    @click="cancelEditNote"
                                >
                                    {{ t("inbox-message-cancel") }}
                                </kv-button>
                            </div>
                        </div>
                    </template>
                </kv-card>

                <kv-card density="compact" class="note-composer-card">
                    <template #text>
                        <div class="note-composer">
                            <kv-textarea
                                v-model="note"
                                :disabled="savingNote"
                                :rows="3"
                                resize
                                :placeholder="
                                    t('inbox-message-note_placeholder')
                                "
                            />
                            <div class="note-submit">
                                <kv-button
                                    size="small"
                                    prepend-icon="ui-plus"
                                    icon-size="2"
                                    :disabled="!savingNote && note.length < 3"
                                    @click="addNote"
                                >
                                    {{ t("inbox-message-add_note") }}
                                </kv-button>
                            </div>
                        </div>
                    </template>
                </kv-card>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState } from "vuex";
import BtnWhatsapp from "../components/btn-whatsapp/btn-whatsapp";
import Text from "../helpers/Text";
import Icon from "../components/icon/icon";
import KvButton from "../components/form/kv-button.vue";
import KvCard from "../components/ui/kv-card.vue";
import KvChip from "../components/ui/kv-chip.vue";
import KvTypography from "../components/ui/kv-typography.vue";
import KvTooltip from "../components/ui/kv-tooltip.vue";
import KvSkeleton from "../components/ui/kv-skeleton.vue";
import KvTextarea from "../components/inputs/kv-textarea.vue";
import KvInput from "../components/inputs/kv-input.vue";
import KvModal from "../components/modal/modal.vue";
import HumanDate from "../components/human-date/human-date";
import AnswerTags from "./answer-tags.vue";
import LinkedAnswerPanel from "../components/answers/linked-answer-panel.vue";
import CreateTaskModal from "../tasks/create-task-modal.vue";
import TableAnswerInput from "../components/table-question/table-answer-input.vue";
import { tableAnswerData } from "../helpers/tableQuestion";
import { isQuestionVisible } from "../helpers/visibilityConditionEvaluator";
import { printAnswer } from "../helpers/printAnswer";
import { mapRfm } from "../helpers/mapper";
import { placeTodayDateString } from "../helpers/datetime";
import {
    fileNameFromUrl,
    formatSelectboxValue,
    isPreviewableImage,
    safeFileUrl,
} from "../helpers/answerDisplay";
import keviApi from "../keviApi";

const ALL_FILE_EXTENSIONS = [
    "jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff",
    "heic", "heif", "avif", "ico", "pdf", "doc", "docx", "xls",
    "xlsx", "ppt", "pptx", "odt", "ods", "odp", "rtf", "txt", "csv",
    "dst", "zip", "rar", "7z", "gz", "mp3", "wav", "ogg", "m4a",
    "aac", "mp4", "mov", "avi", "webm", "mkv",
];
const IMAGE_FILE_EXTENSIONS = [
    "jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff",
    "heic", "heif", "avif", "ico",
];

require("./message.store.js");

export default {
    components: {
        BtnWhatsapp,
        Icon,
        KvButton,
        KvCard,
        KvChip,
        KvTypography,
        KvTooltip,
        KvSkeleton,
        KvTextarea,
        KvInput,
        KvModal,
        HumanDate,
        AnswerTags,
        LinkedAnswerPanel,
        CreateTaskModal,
        TableAnswerInput,
    },
    props: {
        id: [String, Number],
        placeTags: {
            type: Array,
            default: () => [],
        },
        hideClientSection: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        note: "",
        savingNote: false,
        editingNoteId: null,
        editingNoteText: "",
        noteToDelete: null,
        savingEdit: false,
        uploadingEditFiles: {},
        failedEditPreviews: {},
        mutating: false,
        actionsMenuOpen: false,
        editMode: "edit",
        editDraft: {},
        readField: {
            disabled: false,
        },
        tagField: {
            disabled: false,
        },
        npsOptions: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
        cesOptions: [
            { value: 1, label: "Extremamente difícil" },
            { value: 2, label: "Muito difícil" },
            { value: 3, label: "Difícil" },
            { value: 4, label: "Aceitável" },
            { value: 5, label: "Fácil" },
            { value: 6, label: "Muito fácil" },
            { value: 7, label: "Extremamente fácil" },
        ],
    }),
    computed: {
        ...mapState({
            status: (state) => state.message.status,
            message: (state) => state.message.data,
            user: (state) => state.user,
            place: (state) => state.place,
        }),
        canCreateTask() {
            return !!(
                this.$module("tasks") &&
                this.user &&
                this.user.permissions &&
                this.user.permissions.tasks &&
                this.user.permissions.tasks.enabled
            );
        },
        // Only the questions the form would show for the answers being edited:
        // the same visibility conditions as the public form, re-evaluated as
        // the draft changes.
        editableAnswers() {
            const answers = this.editConditionAnswers;
            return (this.message.answers || []).filter(
                (answer) =>
                    answer.type &&
                    answer.type !== "content" &&
                    isQuestionVisible(answer, answers),
            );
        },
        // The draft in the shape the form's condition evaluator expects:
        // keyed by forms_steps_questions id, choices as arrays, numbers as
        // numbers, unanswered as null.
        editConditionAnswers() {
            const answers = {};
            const draft = this.editDraft || {};
            (this.message.answers || []).forEach((answer) => {
                const key = answer.forms_steps_questions_id;
                const value = draft[key];
                if (value === undefined || value === null || value === "") {
                    answers[key] = null;
                } else if (answer.type === "selectbox") {
                    const choices = Array.isArray(value)
                        ? value
                        : String(value).split(", ").filter((item) => item !== "");
                    answers[key] = choices.length ? choices : null;
                } else {
                    answers[key] = value;
                }
            });
            return answers;
        },
        linkedAnswerPanelData() {
            if (!this.message || !this.message.id) {
                return null;
            }
            return {
                id: this.message.id,
                form_name: this.message.form_name,
                grade: this.message.grade,
                created: this.message.created,
                clients_id: this.message.clients_id,
                answers: this.visibleStoredAnswers,
            };
        },
        // Read-only view: answered questions always show; an unanswered one
        // only when the form would have shown it for these answers.
        visibleStoredAnswers() {
            const all = this.message.answers || [];
            const stored = {};
            all.forEach((answer) => {
                const key = answer.forms_steps_questions_id;
                if (!answer.id) {
                    stored[key] = null;
                } else if (["boolean", "emotion", "nps", "ces"].includes(answer.type)) {
                    stored[key] = answer.valueNumber != null ? Number(answer.valueNumber) : null;
                } else if (answer.type === "selectbox") {
                    const choices = String(answer.valueText || "").split(", ").filter((item) => item !== "");
                    stored[key] = choices.length ? choices : null;
                } else {
                    stored[key] = answer.valueText || null;
                }
            });
            return all.filter((answer) => answer.id || isQuestionVisible(answer, stored));
        },
        mergedSuggestions() {
            const byId = {};
            (this.message.available_tags || []).forEach((tag) => {
                byId[String(tag.id)] = tag;
            });
            (this.placeTags || []).forEach((tag) => {
                byId[String(tag.id)] = tag;
            });
            return Object.values(byId);
        },
        rfmInfo() {
            if (!this.message || !this.message.rfm_segment) {
                return null;
            }
            return mapRfm(this.message.rfm_segment);
        },
        // Built once per message so the edit grid does not reset on each keystroke.
        tableSnapshots() {
            const out = {};
            (this.message.answers || []).forEach((answer) => {
                if (answer.type !== "table") {
                    return;
                }
                const data = tableAnswerData(answer);
                if (data) {
                    out[answer.forms_steps_questions_id] = { columns: data.columns, rows: data.rows };
                }
            });
            return out;
        },
        isUploadingEditFiles() {
            return Object.values(this.uploadingEditFiles || {}).some(Boolean);
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        nl2br(text) {
            return Text.nl2br(text);
        },
        // File urls come from the respondent, so anything that is not http(s)
        // never reaches an href.
        safeFiles(answer) {
            return ((answer && answer.files) || [])
                .map((file) => ({
                    url: safeFileUrl(file.url),
                    name: file.name || fileNameFromUrl(file.url),
                }))
                .filter((file) => file.url);
        },
        showsEditPreview(file) {
            return isPreviewableImage(file) && !this.failedEditPreviews[file.url];
        },
        editPreviewFailed(file) {
            this.$set(this.failedEditPreviews, file.url, true);
        },
        draftFiles(answer) {
            const key = answer && answer.forms_steps_questions_id;
            const files = (key && this.editDraft[key]) || [];
            return files
                .map((file) => ({
                    ...file,
                    url: safeFileUrl(file.url),
                    name: file.name || fileNameFromUrl(file.url),
                }))
                .filter((file) => file.url || file.storage_location || file.storage_ref);
        },
        removeEditFile(answer, index) {
            const key = answer && answer.forms_steps_questions_id;
            if (!key) {
                return;
            }
            const files = [...(this.editDraft[key] || [])];
            files.splice(index, 1);
            this.$set(this.editDraft, key, files);
        },
        editFileSettings(answer) {
            let settings = answer && answer.settings;
            if (typeof settings === "string") {
                try {
                    settings = JSON.parse(settings);
                } catch (e) {
                    settings = {};
                }
            }
            return settings || {};
        },
        editFileMaxCount(answer) {
            const settings = this.editFileSettings(answer);
            if (settings.multiple === false) {
                return 1;
            }
            return Math.min(30, Math.max(1, Number(settings.maxFiles) || 30));
        },
        editFileExtensions(answer) {
            const acceptedTypes =
                this.editFileSettings(answer).acceptedTypes || ["any"];
            if (acceptedTypes.includes("any")) {
                return ALL_FILE_EXTENSIONS;
            }
            const extensions = [];
            if (acceptedTypes.includes("image")) {
                extensions.push(...IMAGE_FILE_EXTENSIONS);
            }
            if (acceptedTypes.includes("dst")) {
                extensions.push("dst");
            }
            return Array.from(new Set(extensions));
        },
        editFileAccept(answer) {
            return this.editFileExtensions(answer)
                .map((extension) => "." + extension)
                .join(",");
        },
        canAddEditFiles(answer) {
            const key = answer && answer.forms_steps_questions_id;
            const files = (key && this.editDraft[key]) || [];
            return files.length < this.editFileMaxCount(answer);
        },
        onEditFilesSelected(event, answer) {
            const selected = Array.from(event.target.files || []);
            event.target.value = "";
            if (!selected.length || !answer || !this.message.forms_id) {
                return;
            }

            const key = answer.forms_steps_questions_id;
            const current = [...(this.editDraft[key] || [])];
            const maxFiles = this.editFileMaxCount(answer);
            const room = maxFiles - current.length;
            if (room <= 0) {
                this.$store.dispatch(
                    "notification/error",
                    this.t("inbox-message-upload_files_too_many", [
                        maxFiles,
                    ]),
                );
                return;
            }

            const allowedExtensions = this.editFileExtensions(answer);
            const accepted = selected
                .filter((file) => {
                    const extension = (file.name.split(".").pop() || "").toLowerCase();
                    return allowedExtensions.includes(extension);
                })
                .slice(0, room);
            if (!accepted.length) {
                this.$store.dispatch(
                    "notification/error",
                    this.t("inbox-message-upload_files_error"),
                );
                return;
            }
            const formData = new FormData();
            accepted.forEach((file) => formData.append("files[]", file));
            formData.append("question_id", key);

            this.$set(this.uploadingEditFiles, key, true);
            const options = keviApi.getOptions(
                `form/${this.message.forms_id}/upload`,
                "POST",
                formData,
            );
            this.$http(options)
                .then((response) => {
                    const uploaded =
                        (response.body && response.body.files) || [];
                    if (!uploaded.length) {
                        throw new Error("empty-upload");
                    }
                    this.$set(this.editDraft, key, current.concat(uploaded));
                    if (selected.length > room) {
                        this.$store.dispatch(
                            "notification/error",
                            this.t("inbox-message-upload_files_too_many", [
                                maxFiles,
                            ]),
                        );
                    }
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-upload_files_error"),
                    );
                })
                .finally(() => {
                    this.$set(this.uploadingEditFiles, key, false);
                });
        },
        markAsUnread() {
            this.readField.disabled = true;
            this.$store
                .dispatch("inbox/markUnread", this.message.id)
                .then(() => {
                    this.$store.dispatch(
                        "notification/success",
                        this.t("inbox-notification_marked_unread"),
                    );
                    this.closeMessage();
                })
                .finally(() => {
                    this.readField.disabled = false;
                });
        },
        buildEditDraft() {
            const draft = {};
            (this.message.answers || []).forEach((answer) => {
                const key = answer.forms_steps_questions_id;
                if (answer.type === "file") {
                    draft[key] = answer.files || [];
                    return;
                }
                if (["boolean", "emotion", "nps", "ces"].includes(answer.type)) {
                    draft[key] =
                        answer.valueNumber != null
                            ? Number(answer.valueNumber)
                            : answer.valueNumber;
                    return;
                }
                if (answer.type === "selectbox") {
                    draft[key] = answer.valueText || "";
                    return;
                }
                if (answer.type === "table") {
                    // Edited against the answer's own snapshot of columns and
                    // rows; the server recomputes formulas and summaries.
                    const data = tableAnswerData(answer);
                    if (data) {
                        draft[key] = { values: JSON.parse(JSON.stringify(data.values)) };
                    }
                    return;
                }
                draft[key] = answer.valueText || "";
            });
            return draft;
        },
        buildQuestionsPayload() {
            const payload = { ...this.editDraft };
            const visible = {};
            this.editableAnswers.forEach((answer) => {
                visible[answer.forms_steps_questions_id] = true;
            });
            (this.message.answers || []).forEach((answer) => {
                const key = answer.forms_steps_questions_id;
                if (!visible[key]) {
                    // Hidden by a condition: an old answer is cleared (the
                    // server deletes it on null), an unanswered one is left out.
                    if (answer.id) {
                        payload[key] = null;
                    } else {
                        delete payload[key];
                    }
                    return;
                }
                if (answer.type === "table" && !(payload[key] && Array.isArray(payload[key].values))) {
                    delete payload[key];
                }
            });
            return payload;
        },
        // Columns and rows the answer was given with (not the current question).
        editTableSettings(answer) {
            return this.tableSnapshots[answer.forms_steps_questions_id] || null;
        },
        toggleActionsMenu() {
            this.actionsMenuOpen = !this.actionsMenuOpen;
        },
        closeActionsMenu() {
            this.actionsMenuOpen = false;
        },
        runMenuAction(action) {
            this.closeActionsMenu();
            if (typeof action === "function") {
                action();
            }
        },
        // Clean page with the whole answer and the browser's print dialog
        // ("Save as PDF" included). Same questions as the read-only view.
        printAnswerSheet() {
            const place = this.place || this.$store.state.place || {};
            const timezone = place.timezone || undefined;
            const opened = printAnswer({
                message: this.message,
                answers: this.visibleStoredAnswers,
                t: this.t,
                place,
                formatDate: (value) => {
                    if (!value) return "";
                    const date = new Date(String(value).replace(" ", "T"));
                    if (isNaN(date.getTime())) return String(value);
                    try {
                        return new Intl.DateTimeFormat(this.$root.locale || "pt-BR", {
                            dateStyle: "short",
                            timeStyle: "short",
                            timeZone: timezone,
                        }).format(date);
                    } catch (e) {
                        return date.toLocaleString("pt-BR");
                    }
                },
            });
            if (!opened) {
                this.$store.dispatch("notification/error", this.t("inbox-message-print_blocked"));
            }
        },
        openEditAnswers() {
            this.editMode = "edit";
            this.uploadingEditFiles = {};
            this.editDraft = this.buildEditDraft();
            this.$refs.editAnswersModal.open();
        },
        openDuplicateAnswers() {
            this.editMode = "duplicate";
            this.uploadingEditFiles = {};
            this.editDraft = this.buildEditDraft();
            this.$refs.editAnswersModal.open();
        },
        closeEditModal(toggle) {
            this.editMode = "edit";
            this.uploadingEditFiles = {};
            if (typeof toggle === "function") {
                toggle();
            }
        },
        saveEditedAnswers(toggle) {
            this.savingEdit = true;
            const questions = this.buildQuestionsPayload();
            const isDuplicate = this.editMode === "duplicate";
            const action = isDuplicate
                ? this.$store.dispatch("message/duplicate", questions)
                : this.$store.dispatch("message/editAnswers", questions);

            action
                .then((body) => {
                    this.$store.dispatch(
                        "notification/success",
                        this.t(
                            isDuplicate
                                ? "inbox-message-notification_duplicated"
                                : "inbox-message-notification_edited",
                        ),
                    );
                    if (typeof toggle === "function") {
                        toggle();
                    }
                    this.editMode = "edit";
                    if (isDuplicate) {
                        this.$emit("duplicated", body && body.id);
                        return null;
                    }
                    return this.$store.dispatch("message/get", this.id);
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t(
                            isDuplicate
                                ? "inbox-message-notification_duplicate_error"
                                : "inbox-message-notification_edit_error",
                        ),
                    );
                })
                .finally(() => {
                    this.savingEdit = false;
                });
        },
        openCreateTaskModal() {
            if (!this.$refs.createTaskModal || !this.message) {
                return;
            }
            const clientName = [this.message.first_name, this.message.last_name]
                .filter(Boolean)
                .join(" ")
                .trim();
            this.$refs.createTaskModal.open({
                modalTitle: this.t("inbox-message-create_task_title"),
                title:
                    this.message.form_name ||
                    this.t("inbox-message-create_task_default_title"),
                description: clientName
                    ? this.t("inbox-message-create_task_default_description", [
                          clientName,
                      ])
                    : "",
                clients_id: this.message.clients_id || "",
                answers_id: this.message.id,
                lockClient: true,
                lockedClientLabel: clientName || "",
            });
        },
        onCreateTaskSaved() {
            if (this.id) {
                this.$store.dispatch("message/get", this.id);
            }
        },
        linkedTaskRoute(task) {
            if (task.tasks_boards_id) {
                return {
                    path: "/tasks",
                    query: {
                        view: "board",
                        board_id: String(task.tasks_boards_id),
                        task_id: task.id,
                    },
                };
            }
            return { path: "/tasks", query: { task_id: task.id } };
        },
        linkedTaskCardClass(task) {
            if (task.completed_at) {
                return "is-completed";
            }
            if (Number(task.archived) === 1) {
                return "is-archived";
            }
            if (this.linkedTaskDueClass(task) === "is-overdue") {
                return "is-overdue";
            }
            return "is-open";
        },
        linkedTaskPriorityLabel(task) {
            const priority = Number(task.priority);
            if (priority >= 3) {
                return this.t("tasks-priority_high");
            }
            if (priority === 2) {
                return this.t("tasks-priority_medium");
            }
            return this.t("tasks-priority_low");
        },
        linkedTaskDueClass(task) {
            if (!task || task.completed_at || !task.due_date) {
                return "";
            }
            const timezone =
                (this.place && this.place.timezone) ||
                (this.$store.state.place && this.$store.state.place.timezone);
            const today = placeTodayDateString(timezone);
            const dueDay = String(task.due_date).slice(0, 10);
            if (dueDay < today) {
                return "is-overdue";
            }
            if (dueDay === today) {
                return "is-due-today";
            }
            return "";
        },
        softDeleteAnswer() {
            this.mutating = true;
            const messageId = this.message.id;
            this.$store
                .dispatch("message/softDelete")
                .then(() => {
                    this.$store.commit("inbox/removeMessage", messageId);
                    this.$store.dispatch(
                        "notification/success",
                        this.t("inbox-message-notification_deleted"),
                    );
                    this.closeMessage();
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_delete_error"),
                    );
                })
                .finally(() => {
                    this.mutating = false;
                });
        },
        applyTag(payload) {
            this.tagField.disabled = true;
            this.$store
                .dispatch("message/applyTag", payload)
                .then((body) => {
                    if (body && body.tag) {
                        this.$emit("tags-changed", {
                            tag: body.tag,
                        });
                    }
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagField.disabled = false;
                });
        },
        updateTagDefinition(payload) {
            if (!payload || !payload.id) return;
            this.tagField.disabled = true;
            const options = keviApi.getOptions("forms-tags", "POST", {
                id: payload.id,
                name: payload.name,
                color: payload.color,
            });
            this.$http(options)
                .then((response) => {
                    const tag =
                        (response.body && response.body.tag) || payload;
                    const patchList = (list) =>
                        (list || []).map((item) =>
                            String(item.id) === String(tag.id)
                                ? { ...item, ...tag }
                                : item,
                        );
                    this.$store.commit("message/setData", {
                        ...this.message,
                        tags: patchList(this.message.tags),
                        available_tags: patchList(
                            this.message.available_tags,
                        ),
                    });
                    this.$emit("tags-changed", {
                        tag,
                    });
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagField.disabled = false;
                });
        },
        deleteTagDefinition(tag) {
            if (!tag || !tag.id) return;
            this.tagField.disabled = true;
            const options = keviApi.getOptions(
                "forms-tags/" + tag.id,
                "DELETE",
            );
            this.$http(options)
                .then(() => {
                    const filterList = (list) =>
                        (list || []).filter(
                            (item) => String(item.id) !== String(tag.id),
                        );
                    this.$store.commit("message/setData", {
                        ...this.message,
                        tags: filterList(this.message.tags),
                        available_tags: filterList(
                            this.message.available_tags,
                        ),
                    });
                    this.$emit("tags-changed", {
                        tag: null,
                        deletedTagId: tag.id,
                    });
                })
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagField.disabled = false;
                });
        },
        removeTag(tag) {
            this.tagField.disabled = true;
            this.$store
                .dispatch("message/removeTag", tag.id)
                .then(() => {})
                .catch(() => {
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_tag_error"),
                    );
                })
                .finally(() => {
                    this.tagField.disabled = false;
                });
        },
        addNote() {
            this.savingNote = true;
            this.$store.dispatch("message/addNote", this.note).then(() => {
                window.mixpanel?.track("Add Feedback Note", {
                    message_id: this.id,
                    message_note: this.note,
                    place_id: this.place.id,
                    place_name: this.place.name,
                    place_branch_name: this.user.branch_title,
                    user_name: this.user.name,
                    user_username: this.user.username,
                    user_email: this.user.email,
                    user_global_admin: this.user.global_admin,
                });
                this.savingNote = false;
                this.note = "";
                this.$store.dispatch(
                    "notification/success",
                    this.t("inbox-message-notification_note_saved"),
                );
            });
        },
        editNote(note) {
            this.editingNoteId = note.id;
            this.editingNoteText = this.decodeHtmlEntities(note.note);
        },
        decodeHtmlEntities(text) {
            const textArea = document.createElement("textarea");
            textArea.innerHTML = text;
            return textArea.value;
        },
        saveEditedNote() {
            if (!this.editingNoteText.trim()) {
                this.$store.dispatch(
                    "notification/error",
                    this.t("inbox-message-notification_note_empty"),
                );
                return;
            }

            this.savingNote = true;
            this.$store
                .dispatch("message/updateNote", {
                    noteId: this.editingNoteId,
                    note: this.editingNoteText,
                })
                .then(() => {
                    window.mixpanel?.track("Update Feedback Note", {
                        message_id: this.id,
                        note_id: this.editingNoteId,
                        place_id: this.place.id,
                        place_name: this.place.name,
                        user_name: this.user.name,
                        user_username: this.user.username,
                        user_email: this.user.email,
                        user_global_admin: this.user.global_admin,
                    });
                    this.cancelEditNote();
                    this.savingNote = false;
                    this.$store.dispatch(
                        "notification/success",
                        this.t("inbox-message-notification_note_updated"),
                    );
                })
                .catch(() => {
                    this.savingNote = false;
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_note_update_error"),
                    );
                });
        },
        cancelEditNote() {
            this.editingNoteId = null;
            this.editingNoteText = "";
        },
        confirmDeleteNote(note) {
            this.noteToDelete = note;
            this.$refs.confirmDeleteNote.open();
        },
        deleteNote() {
            if (!this.noteToDelete) {
                return;
            }

            this.savingNote = true;
            this.$store
                .dispatch("message/deleteNote", {
                    noteId: this.noteToDelete.id,
                })
                .then(() => {
                    window.mixpanel?.track("Delete Feedback Note", {
                        message_id: this.id,
                        note_id: this.noteToDelete.id,
                        place_id: this.place.id,
                        place_name: this.place.name,
                        user_name: this.user.name,
                        user_username: this.user.username,
                        user_email: this.user.email,
                        user_global_admin: this.user.global_admin,
                    });
                    this.noteToDelete = null;
                    this.savingNote = false;
                    this.$store.dispatch(
                        "notification/success",
                        this.t("inbox-message-notification_note_deleted"),
                    );
                })
                .catch(() => {
                    this.savingNote = false;
                    this.$store.dispatch(
                        "notification/error",
                        this.t("inbox-message-notification_note_delete_error"),
                    );
                });
        },
        closeMessage() {
            this.$emit("close");
        },
        openTaskFile(file) {
            if (file && /^https?:\/\//i.test(file.url || "")) {
                window.open(file.url, "_blank", "noopener,noreferrer");
            }
        },
        fomatAnswerValue(answer) {
            return formatSelectboxValue(answer);
        },
    },
    watch: {
        id() {
            if (this.id) {
                this.$store.dispatch("message/get", this.id);
            }
        },
    },
    mounted() {
        if (this.id) {
            this.$store.dispatch("message/get", this.id);
        }
    },
};
</script>

<style scoped>
.linked-task-files {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.linked-task-file {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 6px;
    background: #f3f4f6;
    color: var(--primary);
    font-size: 12px;
    cursor: pointer;
}
</style>
